import { useState, useRef, useEffect } from 'react'
import { Play, Calendar } from 'lucide-react'

interface TimerDisplayProps {
  elapsedSeconds: number
  startedAt?: string | null
  onUpdateStartedAt?: (startedAt: string) => Promise<void>
  className?: string
  isIdle?: boolean
  isPaused?: boolean
}

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatStartTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function TimerDisplay({ elapsedSeconds, startedAt, onUpdateStartedAt, className, isIdle, isPaused }: TimerDisplayProps) {
  const hours = Math.floor(elapsedSeconds / 3600)
  const minutes = Math.floor((elapsedSeconds % 3600) / 60)
  const seconds = elapsedSeconds % 60

  const format = (n: number) => n.toString().padStart(2, '0')

  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.showPicker?.()
      inputRef.current.focus()
    }
  }, [editing])

  const handleStartEdit = () => {
    if (startedAt && onUpdateStartedAt) {
      setEditValue(toDatetimeLocal(startedAt))
      setEditing(true)
    }
  }

  const handleSave = () => {
    setEditing(false)
    if (!editValue || !onUpdateStartedAt) return
    const parsed = new Date(editValue)
    if (isNaN(parsed.getTime())) return
    onUpdateStartedAt(parsed.toISOString())
  }

  if (isIdle) {
    return (
      <div className={`flex flex-col items-center gap-3 ${className || ''}`} data-testid="timer-display">
        <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-indigo-50 to-indigo-100 ring-2 ring-indigo-200 ring-offset-2">
          <Play className="w-9 h-9 text-indigo-500 ml-1" />
        </div>
        <span className="text-sm font-medium text-muted-foreground">Ready to start</span>
      </div>
    )
  }

  return (
    <div className={`flex flex-col items-center gap-2 ${className || ''}`} data-testid="timer-display">
      <div className={`w-full rounded-xl px-6 py-4 ${isPaused ? 'bg-amber-50 border border-amber-200' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200'}`}>
        <div className="flex items-center justify-center gap-3">
          <div className={`h-3 w-3 rounded-full ${isPaused ? 'bg-amber-400' : 'bg-green-500'} ${isPaused ? '' : 'animate-pulse'}`} />
          <div className="font-mono text-4xl font-bold tracking-tight text-gray-900">
            {format(hours)}:{format(minutes)}:{format(seconds)}
          </div>
        </div>
      </div>

      {startedAt && onUpdateStartedAt && (
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {editing ? (
            <input
              ref={inputRef}
              type="datetime-local"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
                if (e.key === 'Escape') setEditing(false)
              }}
              className="h-6 w-44 rounded border border-input bg-transparent px-1 text-xs text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
          ) : (
            <button
              onClick={handleStartEdit}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer underline decoration-dotted underline-offset-2"
              title="Click to edit start time"
            >
              Started {formatStartTime(startedAt)}
            </button>
          )}
        </div>
      )}

      <span className={`text-xs font-medium ${isPaused ? 'text-amber-600' : 'text-green-600'}`}>
        {isPaused ? 'Paused' : 'Tracking'}
      </span>
    </div>
  )
}

export default TimerDisplay